import { Button, ColorInput, Group, Stack, Switch, Tabs, TextInput, Textarea } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useState, useMemo, useEffect } from 'react';
import { useGetSupportedLanguagesQuery } from '@codezo/graphql';

interface CategoryUpdateFormProps {
	initialValues: any;
	onSubmit: (values: any) => void;
	onCancel: () => void;
	loading?: boolean;
}

interface CategoryTranslation {
	name: string;
}

interface CategoryFormData {
	color: string;
	headerColor: string;
	cardColor: string;
	isActive: boolean;
	headerSvg: string;
	illustrationSvg: string;
	translations: Record<string, CategoryTranslation>; // Dynamic language codes
}

export function CategoryUpdateForm({
	initialValues,
	onSubmit,
	onCancel,
	loading,
}: CategoryUpdateFormProps) {
	const [activeTab, setActiveTab] = useState<string>('en');

	// Fetch supported languages from backend
	const { data: languagesData } = useGetSupportedLanguagesQuery();

	// Derive languages and labels from query
	const LANGUAGES = useMemo(
		() => languagesData?.getSupportedLanguages?.map((lang) => lang.code) || ['en', 'da'],
		[languagesData]
	);

	const LANGUAGE_LABELS = useMemo(
		() =>
			languagesData?.getSupportedLanguages?.reduce(
				(acc, lang) => ({
					...acc,
					[lang.code]: lang.name,
				}),
				{} as Record<string, string>
			) || { en: 'English', da: 'Danish' },
		[languagesData]
	);

	// Transform initialValues from GraphQL format to form format (dynamically based on LANGUAGES)
	const transformedInitialValues = useMemo(() => {
		// Build translations object dynamically
		const buildTranslations = (source?: any[]) => {
			const translations: any = {};
			LANGUAGES.forEach((lang) => {
				const sourceTranslation = source?.find((t: any) => t.language === lang);
				// Strip __typename and other GraphQL metadata
				translations[lang] = sourceTranslation
					? { name: sourceTranslation.name || '' }
					: { name: '' };
			});
			return translations;
		};

		return {
			color: initialValues.color || '#000000',
			headerColor: initialValues.headerColor || '#000000',
			cardColor: initialValues.cardColor || '#000000',
			isActive: initialValues.isActive ?? true,
			headerSvg: initialValues.headerSvg || '',
			illustrationSvg: initialValues.illustrationSvg || '',
			translations: buildTranslations(initialValues.translations),
		};
	}, [initialValues, LANGUAGES]);

	const form = useForm<CategoryFormData>({
		initialValues: transformedInitialValues,
		validate: {
			color: (value) => (!value ? 'Color is required' : null),
			translations: {
				en: {
					name: (value) => (!value ? 'English name is required' : null),
				},
			},
		},
	});

	// Update form values when initialValues change
	useEffect(() => {
		form.setValues(transformedInitialValues);
		form.resetDirty();
	}, [transformedInitialValues]);

	const handleSubmit = (values: CategoryFormData) => {
		// Transform form data to match GraphQL input format
		const translations = LANGUAGES.map((lang) => ({
			language: lang,
			name: values.translations[lang].name,
		}));

		onSubmit({
			id: initialValues.id,
			color: values.color,
			headerColor: values.headerColor || null,
			cardColor: values.cardColor || null,
			headerSvg: values.headerSvg || null,
			illustrationSvg: values.illustrationSvg || null,
			translations,
		});
	};

	return (
		<form onSubmit={form.onSubmit(handleSubmit)} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
			<div style={{ flex: 1, overflowY: 'auto', padding: '0 1.5rem', paddingBottom: '1.5rem' }}>
				<Stack gap="md">
					{/* Color Fields */}
					<ColorInput
						label="Color"
						placeholder="Pick a color"
						required
						{...form.getInputProps('color')}
					/>

					<ColorInput
						label="Header Color"
						placeholder="Pick a header color"
						{...form.getInputProps('headerColor')}
					/>

					<ColorInput
						label="Card Color"
						placeholder="Pick a card color"
						{...form.getInputProps('cardColor')}
					/>

					{/* Active Status */}
					{/* <Switch
						label="Active"
						description="Inactive categories will not be visible in the app"
						{...form.getInputProps('isActive', { type: 'checkbox' })}
					/> */}

					{/* SVG Fields */}
					<Textarea
						label="Header SVG"
						description="Paste the SVG XML for the category header"
						placeholder="<svg>...</svg>"
						minRows={3}
						maxRows={8}
						autosize
						{...form.getInputProps('headerSvg')}
					/>

					<Textarea
						label="Illustration SVG"
						description="Paste the SVG XML for the category illustration"
						placeholder="<svg>...</svg>"
						minRows={3}
						maxRows={8}
						autosize
						{...form.getInputProps('illustrationSvg')}
					/>

					{/* Translation Tabs */}
					<Tabs value={activeTab} onChange={(value) => setActiveTab(value || 'en')}>
						<Tabs.List>
							{LANGUAGES.map((lang) => (
								<Tabs.Tab key={lang} value={lang}>
									{LANGUAGE_LABELS[lang]}
								</Tabs.Tab>
							))}
						</Tabs.List>

						{LANGUAGES.map((lang) => (
							<Tabs.Panel key={lang} value={lang} pt="md">
								<Stack gap="md">
									<TextInput
										label="Category Name"
										placeholder={`Category name in ${LANGUAGE_LABELS[lang]}`}
										required={lang === 'en'}
										{...form.getInputProps(`translations.${lang}.name`)}
									/>
								</Stack>
							</Tabs.Panel>
						))}
					</Tabs>
				</Stack>
			</div>

			{/* Action Buttons - Fixed at bottom */}
			<Group
				justify="flex-end"
				style={{ borderTop: '1px solid #dee2e6', padding: '1rem 1.5rem', marginTop: 'auto' }}
			>
				<Button variant="subtle" onClick={onCancel} disabled={loading}>
					Cancel
				</Button>
				<Button type="submit" loading={loading}>
					Update Category
				</Button>
			</Group>
		</form>
	);
}
